/*
  Thin fetch wrappers around the Insites Ecommerce Add-on API endpoints
  copied into modules/ecommerce/public/views/pages/api/ (Phase 1).

  Contract for every endpoint:
    - Same-origin POST/PUT/GET, JSON body, JSON response
    - Addon's pages set `format: json` — they always return parseable JSON
    - CSRF is carried automatically by the _pos_session cookie on same-origin
      requests; React pages do not need to inject authenticity_token for
      these page-based JSON endpoints (forms are a different story — see
      `submitCheckoutPayment` below, which targets a platformOS form).

  All wrappers return the parsed body; callers handle success/error shape.
  Errors on network / non-2xx throw with a user-safe message — bubble up to
  the component so it can render a banner.
*/

import type { Cart } from '@/types/ecommerce'

export class EcommerceApiError extends Error {
  status: number
  body: unknown
  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'EcommerceApiError'
    this.status = status
    this.body = body
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { parseEmpty?: boolean } = {},
): Promise<T> {
  const { parseEmpty, ...rest } = init
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(rest.headers ?? {}),
    },
    ...rest,
  })

  const text = await res.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  } else if (parseEmpty) {
    body = {}
  }

  if (!res.ok) {
    const message =
      (body && typeof body === 'object' && 'error' in body && typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : res.statusText) || 'Request failed'
    throw new EcommerceApiError(message, res.status, body)
  }

  // The ecommerce add-on's API endpoints respond with HTTP 200 even when the
  // request was rejected at the controller level — the error is surfaced as
  // an `error` key in the body (e.g. `{"error":"contact_uuid is required"}`).
  // Treat that as a failure so the caller can handle it instead of silently
  // continuing as if the cart mutation succeeded.
  if (body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)) {
    const err = (body as { error: unknown }).error
    if (err && typeof err === 'string') {
      throw new EcommerceApiError(err, res.status, body)
    }
  }

  return body as T
}

/* ---------- Cart ----------

   The add-on requires `contact_uuid` on every cart-mutating call: for
   logged-in users that is the platformOS contact's external_id, for
   guests a `guest-<uuid>` value persisted on the session (resolved by
   `modules/ecommerce/user/get_user_uuid`). React reads it from
   `useAuth().contactUuid` and passes it through. */

export interface AddToCartInput {
  product_uuid: string
  product_variant_uuid?: string | null
  quantity: number
  contact_uuid: string
}

export function addToCart(input: AddToCartInput): Promise<{ cart?: Cart; [k: string]: unknown }> {
  return request('/api/add-to-cart', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export interface RemoveFromCartInput {
  /** cart_items.id — the addon endpoint validates id + uuid + cart_uuid
   *  against an existing cart_item before deleting; sending only one of
   *  them makes it return "Unauthorized" silently. */
  id: string
  /** cart_items.uuid */
  uuid: string
  /** parent cart.uuid */
  cart_uuid: string
  contact_uuid: string
}

export function removeFromCart(input: RemoveFromCartInput) {
  return request('/api/remove-to-cart', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/* ---------- Contacts / Companies ---------- */

export interface CreateContactInput {
  first_name: string
  last_name: string
  email: string
  mobile_phone_country_code?: string
  mobile_phone_number?: string
  /** Optional. Forwarded to the CRM controller; if a matching company
   *  doesn't already exist, the addon's create flow handles the
   *  create-and-associate. */
  company_name?: string
}

export interface ContactResult {
  id?: string
  uuid?: string
  first_name?: string
  last_name?: string
  email?: string
  [k: string]: unknown
}

export function createContact(input: CreateContactInput): Promise<ContactResult> {
  return request('/api/create-contact', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export interface UpdateContactInput {
  /** UUID of the existing CRM contact to update. The addon's
   *  /api/update-contact endpoint reads `payload.uuid` server-side to
   *  resolve which contact to mutate, then forwards the rest of the
   *  payload into the `crm/controller/contacts/update` controller. */
  uuid: string
  first_name?: string
  last_name?: string
  email?: string
  mobile_phone_country_code?: string
  mobile_phone_number?: string
  company_name?: string
}

export function updateContact(input: UpdateContactInput) {
  return request('/api/update-contact', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

/* Free-form contact update — used by the checkout flow to set
   `checkout_shipping_address_uuid`, `checkout_billing_address_uuid`,
   and `checkout_shipping_instructions` on the buyer's CRM contact so
   the payment form's `default_payload` can resolve them at submit
   time without depending on (broken) session writes. */
export interface UpdateContactPropertiesInput {
  uuid: string
  [key: string]: unknown
}

export function updateContactProperties(input: UpdateContactPropertiesInput) {
  return request('/api/update-contact', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

export function createCompany(input: { company_name: string; [k: string]: unknown }) {
  return request('/api/create-company', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/* ---------- Addresses ---------- */

export interface CreateAddressInput {
  related_uuid: string
  related_type: string
  address_label: string
  address_1: string
  address_2?: string
  suburb: string
  state: string
  postcode: string
  country: string
  is_default_shipping?: boolean
  is_default_billing?: boolean
}

export interface CreateAddressResponse {
  id?: string | number | null
  uuid?: string | null
  [k: string]: unknown
}

export function createContactAddress(input: CreateAddressInput): Promise<CreateAddressResponse> {
  return request<CreateAddressResponse>('/api/create-contact-address', {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export interface UpdateAddressInput {
  /** UUID of the existing crm_address record to mutate. */
  uuid: string
  address_label?: string
  address_1?: string
  address_2?: string
  suburb?: string
  state?: string
  postcode?: string
  country?: string
  is_default_shipping?: boolean
  is_default_billing?: boolean
}

/** Updates an existing CRM address record in-place. Used by the
 *  checkout flow to keep a guest buyer's shipping/billing record
 *  count at exactly one per session — when they back-nav and submit
 *  again with different details, we mutate the same row instead of
 *  creating a duplicate. */
export function updateContactAddress(input: UpdateAddressInput): Promise<CreateAddressResponse> {
  return request<CreateAddressResponse>('/api/update-contact-address', {
    method: 'PUT',
    body: JSON.stringify(input),
  })
}

/* ---------- Email validation ---------- */

export interface ValidateEmailUser {
  id: string
  external_id: string
  email: string
  /** Empty when the email matches a CRM contact that has not signed
   *  up for an account; non-empty when the same email already has a
   *  portal profile (in which case the user should sign in instead). */
  profiles: unknown[]
}

export interface ValidateEmailResponse {
  items: { results: ValidateEmailUser[] }
}

export function validateEmail(email: string): Promise<ValidateEmailResponse> {
  const params = new URLSearchParams({ email })
  return request(`/api/validate-email?${params.toString()}`, { method: 'GET' })
}

/* ---------- Checkout session ---------- */

export interface SaveCheckoutSessionInput {
  contact_email?: string
  cart_uuid?: string
  [k: string]: unknown
}

export function saveCheckoutSession(input: SaveCheckoutSessionInput) {
  return request('/api/save-checkout-session', {
    method: 'POST',
    body: JSON.stringify(input),
    parseEmpty: true,
  })
}

/* MIG-only: GET sibling of saveCheckoutSession that hits a regular
   Liquid page (no `format: json`). Session writes from format:json
   endpoints are silently dropped on this MIG instance, but writes
   from plain GET pages DO persist (verified). React calls BOTH so
   when the platform-side session bug is resolved the JSON path
   continues to work. */
export function syncCheckoutSession(input: Record<string, string | undefined>) {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(input)) {
    if (v != null && v !== '') params.set(k, String(v))
  }
  return request(`/api/sync-checkout-session?${params.toString()}`, {
    method: 'GET',
    parseEmpty: true,
  })
}

/* ---------- Stripe ---------- */

export function deleteStripeCreditCard(payment_method_uuid: string) {
  return request('/api/ecommerce/stripe/delete-credit-card', {
    method: 'POST',
    body: JSON.stringify({ payment_method_uuid }),
  })
}

/* ---------- Availability (MIG one-of-a-kind guard) ---------- */

export interface AvailabilityCheckResult {
  ok: boolean
  /** Backwards-compat — kept identical to `unavailable`. */
  sold: Array<{ product_uuid: string; status: string; reason?: 'sold' | 'disabled' }>
  /** Canonical field. Each entry carries a `reason` so the UI can pick
   *  reason-specific copy ("sold to another buyer" vs "no longer
   *  available"). */
  unavailable?: Array<{ product_uuid: string; status: string; reason: 'sold' | 'disabled' }>
}

/* Asks the MIG-side endpoint whether any of the supplied products have
   been marked Sold since they were added to the cart. Used to block
   the payment submit and to flag sold rows on /cart. */
export function checkCartAvailability(
  productUuids: string[],
): Promise<AvailabilityCheckResult> {
  const cleaned = productUuids.filter(Boolean)
  if (cleaned.length === 0) return Promise.resolve({ ok: true, sold: [] })
  const url = `/api/cart/check-availability?uuids=${encodeURIComponent(cleaned.join(','))}`
  return request<AvailabilityCheckResult>(url, { method: 'GET' })
}
